import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Product } from '../types';
import { supabase } from '../lib/supabaseClient';
import { Helmet } from 'react-helmet-async';
import { useContent } from '../hooks/useContent';
import { LayoutGrid, type LucideIcon } from 'lucide-react';
import * as Icons from 'lucide-react';
import {
  fetchProductsListPage,
  mapListRowToProduct,
  PRODUCTS_PAGE_SIZE,
  type ProductsListSortKey,
} from '../lib/productsListQuery';
import ProductGrid from '../components/ProductGrid';
import SiteContentText from '../components/SiteContentText';
import { trackViewItemList } from '../utils/ga4Ecommerce';
import { trackSearch } from '../utils/siteAnalytics';
import { Link, useSearchParams } from 'react-router-dom';
import {
  peekPendingDiscountCode,
} from '../utils/rebookPromo';
import PendingPromoBanner from '../components/PendingPromoBanner';
import { LIGHT_PRODUCT_USE_CASE_BLURBS } from '../config/lightCommercialRouting';

const JSON_LD_MAX_ITEMS = PRODUCTS_PAGE_SIZE;

const ProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [listRefreshing, setListRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialCategory = (searchParams.get('category') || '').trim();
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);
  const [categoryOrder, setCategoryOrder] = useState<string[]>([]);
  const [categoryIcons, setCategoryIcons] = useState<Record<string, string>>({});
  const initialSearch = (searchParams.get('search') || '').trim();
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [sortBy, setSortBy] = useState<ProductsListSortKey>('popular');
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const { content: productsContent } = useContent(undefined, 'products');

  const replaceAbortRef = useRef<AbortController | null>(null);
  const initialDoneRef = useRef(false);

  const [showPendingPromo, setShowPendingPromo] = useState(() => Boolean(peekPendingDiscountCode()));

  useEffect(() => {
    const fromQuery = (searchParams.get('rabatt') || searchParams.get('discount') || '').trim();
    if (fromQuery || peekPendingDiscountCode()) {
      setShowPendingPromo(true);
    }
  }, [searchParams]);

  const getCategoryIcon = (category: string): LucideIcon => {
    if (category === 'Alla') return LayoutGrid;

    const iconName = categoryIcons[category];
    if (!iconName) return LayoutGrid;

    const formattedIconName = iconName
      .split('-')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');

    const IconComponent = (Icons as Record<string, LucideIcon>)[formattedIconName];
    return IconComponent || LayoutGrid;
  };

  useEffect(() => {
    document.title = 'Hyra festutrustning – ljud, ljus & DJ i Skåne | festutrustning.se';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Bläddra bland PA, uplights, DJ-system och effekter. Filtrera på kategori, se pris per dygn och boka festutrustning online med leverans i Skåne.'
      );
    }

    if (typeof window !== 'undefined') {
      (window as unknown as { prerenderReady?: boolean }).prerenderReady = false;
    }

    const timeout = setTimeout(() => {
      if (typeof window !== 'undefined') {
        (window as unknown as { prerenderReady?: boolean }).prerenderReady = true;
      }
    }, 10000);

    void fetchCategories();

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Håll ?search= i synk med debounced sök (WebsiteSchema använder redan parametern).
  useEffect(() => {
    const current = (searchParams.get('search') || '').trim();
    if (current === debouncedSearch) return;
    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) next.set('search', debouncedSearch);
    else next.delete('search');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when debouncedSearch changes
  }, [debouncedSearch, setSearchParams]);

  // Håll ?category= i synk så filter överlever bakåt från produktsida.
  useEffect(() => {
    const current = (searchParams.get('category') || '').trim();
    if (current === selectedCategory) return;
    const next = new URLSearchParams(searchParams);
    if (selectedCategory) next.set('category', selectedCategory);
    else next.delete('category');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when selectedCategory changes
  }, [selectedCategory, setSearchParams]);

  const lastTrackedSearchRef = useRef('');
  useEffect(() => {
    if (!debouncedSearch || debouncedSearch === lastTrackedSearchRef.current) return;
    if (totalCount === null) return;
    lastTrackedSearchRef.current = debouncedSearch;
    trackSearch(debouncedSearch, totalCount);
  }, [debouncedSearch, totalCount]);

  const applyJsonLd = useCallback((sliceForSchema: Product[], numberOfItems: number) => {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Festutrustning produkter',
      description:
        'Utvalda produkter för uthyrning i Skåne (fullständigt sortiment på sidan med filter och sök).',
      numberOfItems,
      itemListElement: sliceForSchema.map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.name,
          description: product.shortDescription,
          image: product.image,
          url: `https://festutrustning.se/produkter/${product.slug || product.id}`,
          offers: {
            '@type': 'Offer',
            price: product.pricePerDay,
            priceCurrency: 'SEK',
            availability: 'https://schema.org/InStock',
          },
        },
      })),
    };

    const existingScript = document.querySelector('script[data-products-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-products-schema', 'true');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }, []);

  const fetchCategories = async () => {
    try {
      const { data, error: catErr } = await supabase
        .from('product_categories')
        .select('name, icon')
        .order('sort_order', { ascending: true });

      if (catErr) throw catErr;

      const iconMap: Record<string, string> = {};
      const names: string[] = [];
      data?.forEach((cat) => {
        iconMap[cat.name] = cat.icon;
        names.push(cat.name);
      });
      setCategoryIcons(iconMap);
      setCategoryOrder(names);
    } catch (err: unknown) {
      console.error('Error fetching product categories:', err);
    }
  };

  const loadFirstPage = useCallback(async () => {
    replaceAbortRef.current?.abort();
    const controller = new AbortController();
    replaceAbortRef.current = controller;

    const isInitial = !initialDoneRef.current;
    if (isInitial) {
      setLoading(true);
    } else {
      setListRefreshing(true);
    }
    setError(null);

    try {
      const isTieredFetchCategory =
        selectedCategory.trim() === 'Ljud' ||
        selectedCategory.trim() === 'Ljus' ||
        selectedCategory.trim() === 'Mikrofon' ||
        selectedCategory.trim() === 'Scen / Tross' ||
        selectedCategory.trim() === 'Effekter';

      if (isTieredFetchCategory) {
        // Full catalog in one sweep — no inter-tier pagination.
        const allMapped: Product[] = [];
        let offset = 0;
        let total: number | null = null;
        const chunk = 50;

        while (true) {
          const { data, error: qErr, count } = await fetchProductsListPage(
            supabase,
            {
              category: selectedCategory,
              search: debouncedSearch,
              sortBy,
              offset,
              pageSize: chunk,
            },
            { signal: controller.signal }
          );
          if (qErr) throw qErr;
          if (controller.signal.aborted) return;

          if (typeof count === 'number') total = count;
          const rows = data;
          const hasNext = rows.length > chunk;
          const pageRows = rows.slice(0, chunk);
          for (const row of pageRows) {
            allMapped.push(mapListRowToProduct(row));
          }
          if (!hasNext || pageRows.length === 0) break;
          offset += chunk;
          if (offset > 2000) break;
        }

        setProducts(allMapped);
        setHasMore(false);
        setTotalCount(total ?? allMapped.length);

        const schemaSlice = allMapped.slice(0, JSON_LD_MAX_ITEMS);
        applyJsonLd(schemaSlice, total ?? allMapped.length);
        trackViewItemList(allMapped, selectedCategory.trim());
        initialDoneRef.current = true;
        return;
      }

      const { data, error: qErr, count } = await fetchProductsListPage(
        supabase,
        {
          category: selectedCategory,
          search: debouncedSearch,
          sortBy,
          offset: 0,
          pageSize: PRODUCTS_PAGE_SIZE,
        },
        { signal: controller.signal }
      );

      if (qErr) throw qErr;
      if (controller.signal.aborted) return;

      const rows = data;
      const hasNext = rows.length > PRODUCTS_PAGE_SIZE;
      const pageRows = rows.slice(0, PRODUCTS_PAGE_SIZE);
      const mapped = pageRows.map(mapListRowToProduct);

      setProducts(mapped);
      setHasMore(hasNext);
      setTotalCount(typeof count === 'number' ? count : mapped.length);

      const schemaSlice = mapped.slice(0, JSON_LD_MAX_ITEMS);
      const schemaTotal = typeof count === 'number' ? count : mapped.length;
      applyJsonLd(schemaSlice, schemaTotal);

      const listName = selectedCategory.trim() || 'Alla produkter';
      trackViewItemList(mapped, listName);

      initialDoneRef.current = true;
    } catch (err: unknown) {
      if (controller.signal.aborted) return;
      console.error('Error fetching products:', err);
      setError('Ett fel uppstod när produkterna skulle hämtas');
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setListRefreshing(false);
        if (typeof window !== 'undefined') {
          (window as unknown as { prerenderReady?: boolean }).prerenderReady = true;
        }
      }
    }
  }, [selectedCategory, debouncedSearch, sortBy, applyJsonLd]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  const loadMore = useCallback(async () => {
    if (
      selectedCategory.trim() === 'Ljud' ||
      selectedCategory.trim() === 'Ljus' ||
      selectedCategory.trim() === 'Mikrofon' ||
      selectedCategory.trim() === 'Scen / Tross' ||
      selectedCategory.trim() === 'Effekter'
    ) {
      return;
    }
    if (!hasMore || loadingMore || listRefreshing) return;

    setLoadingMore(true);
    setError(null);

    try {
      const offset = products.length;
      const { data, error: qErr } = await fetchProductsListPage(supabase, {
        category: selectedCategory,
        search: debouncedSearch,
        sortBy,
        offset,
        pageSize: PRODUCTS_PAGE_SIZE,
      });

      if (qErr) throw qErr;

      const rows = data;
      const hasNext = rows.length > PRODUCTS_PAGE_SIZE;
      const pageRows = rows.slice(0, PRODUCTS_PAGE_SIZE);
      const mapped = pageRows.map(mapListRowToProduct);

      const listName = selectedCategory.trim() || 'Alla produkter';
      trackViewItemList(mapped, `${listName} (sida ${Math.floor(offset / PRODUCTS_PAGE_SIZE) + 2})`);

      setProducts((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const merged = [...prev];
        for (const p of mapped) {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            merged.push(p);
          }
        }
        return merged;
      });
      setHasMore(hasNext);
    } catch (err: unknown) {
      console.error('Error loading more products:', err);
      setError('Kunde inte ladda fler produkter');
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, listRefreshing, products.length, selectedCategory, debouncedSearch, sortBy]);

  const getContent = (key: string, fallback: string = '') => {
    const item = productsContent.find((c) => c.key === key);
    return item?.content || fallback;
  };

  const categoryTabs = useMemo(() => {
    if (categoryOrder.length > 0) {
      return ['Alla', ...categoryOrder];
    }
    const fromProducts = [...new Set(products.map((p) => p.category))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'sv'));
    return ['Alla', ...fromProducts];
  }, [categoryOrder, products]);

  if (loading) {
    return (
      <div className="pt-12 pb-24 bg-transparent min-h-screen">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="text-center mb-16">
              <div className="h-16 bg-white/20 rounded w-3/4 mx-auto mb-6"></div>
              <div className="h-8 bg-white/20 rounded w-full max-w-2xl mx-auto mb-8"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-slate-900/35 border border-slate-800 p-6">
                    <div className="h-12 bg-white/20 rounded w-1/2 mx-auto mb-4"></div>
                    <div className="h-6 bg-white/20 rounded w-3/4 mx-auto"></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-900/35 border border-slate-800 p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-slate-800/50 rounded-none h-80 border border-slate-700"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="pt-12 pb-24 bg-transparent min-h-screen">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="bg-red-500/20 border border-red-500/30 p-8 text-red-200 max-w-2xl mx-auto">
              <h1 className="text-2xl font-bold mb-4">Ett fel uppstod</h1>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-12 pb-24 min-h-screen bg-transparent relative overflow-hidden">
      <Helmet>
        <title>Hyra festutrustning – ljud, ljus & DJ i Skåne | festutrustning.se</title>
        <meta
          name="description"
          content="Bläddra bland PA, uplights, DJ-system och effekter. Filtrera på kategori, se pris per dygn och boka festutrustning online med leverans i Skåne."
        />
        <link rel="canonical" href="https://festutrustning.se/produkter" />
      </Helmet>

      <div className="hidden">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-10 left-1/2 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      <div className="hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white rounded-full opacity-30 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4">
        {showPendingPromo && (
          <div className="relative z-10 mb-8">
            <PendingPromoBanner />
          </div>
        )}
        <div className="text-center mb-12 relative z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            {getContent('products_title', 'Våra produkter och paket')}
          </h1>
          <SiteContentText
            as="p"
            className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed"
            text={getContent(
              'products_subtitle',
              'Bläddra bland vårt sortiment av professionell ljudutrustning, ljussystem, DJ-utrustning och effekter. Vi har allt du behöver för en lyckad fest.'
            )}
          />
          <p className="mt-4 text-sm md:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Behöver du främst ljud? Se våra lösningar för att{' '}
            <Link
              to="/hyra-hogtalare-malmo"
              className="text-purple-300 underline decoration-purple-500/50 underline-offset-2 hover:text-purple-200"
            >
              hyra högtalare i Malmö
            </Link>
            , från enklare musik till fullt dansgolv. Behöver ni hyra ljudanläggning eller PA till fest? Se{' '}
            <Link
              to="/ljud-ljus-malmo"
              className="text-purple-300 underline decoration-purple-500/50 underline-offset-2 hover:text-purple-200"
            >
              hyra ljud och PA i Malmö
            </Link>
            . Vill ni ha ljud, ljus och effekt i ett paket? Se{' '}
            <Link
              to="/kompletta-festpaket"
              className="text-purple-300 underline decoration-purple-500/50 underline-offset-2 hover:text-purple-200"
            >
              kompletta festpaket
            </Link>
            .
          </p>
        </div>

        <div className="relative z-10 mb-8">
          <div className="bg-slate-900/40 backdrop-blur-lg p-6 md:p-8 border border-slate-800">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">Välj kategori</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {categoryTabs.map((category) => {
                const Icon = getCategoryIcon(category);
                const isActive = selectedCategory === category || (category === 'Alla' && selectedCategory === '');

                return (
                  <button
                    key={category}
                    type="button"
                    className={`group relative flex flex-col items-center justify-center p-4 rounded-none text-sm font-medium transition-all duration-300 ${
                      isActive
                        ? 'bg-slate-900/60 text-white border border-purple-500/40 scale-105'
                        : 'bg-slate-900/20 text-slate-200 hover:bg-slate-900/40 border border-slate-800'
                    }`}
                    onClick={() => setSelectedCategory(category === 'Alla' ? '' : category)}
                  >
                    <Icon
                      size={28}
                      className={`mb-2 transition-transform duration-300 ${
                        isActive ? 'text-white' : 'text-purple-600 group-hover:scale-110'
                      }`}
                    />
                    <span className="text-center leading-tight">{category}</span>
                    {isActive && (
                      <div className="absolute inset-0 rounded-none bg-purple-500/10 pointer-events-none border border-purple-500/10" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {selectedCategory === 'Ljus' ? (
          <div
            className="relative z-10 mb-6 border border-slate-800 bg-slate-900/40 p-5 md:p-6"
            data-testid="ljus-use-case-blurbs"
          >
            <p className="mb-4 text-center text-sm text-slate-300">
              Ljus efter användning – välj utifrån hur lokalen ska kännas, inte bara modellnamn.
            </p>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {LIGHT_PRODUCT_USE_CASE_BLURBS.map((row) => (
                <li key={row.title} className="text-sm text-slate-400">
                  <span className="font-semibold text-slate-200">{row.title}.</span> {row.blurb}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-center text-xs text-slate-500">
              Osäker på nivå? Se{' '}
              <Link
                to="/ljud-ljus-skane"
                className="text-purple-300 underline decoration-purple-500/50 underline-offset-2 hover:text-purple-200"
              >
                ljud &amp; ljus i Skåne
              </Link>{' '}
              eller{' '}
              <Link
                to="/kompletta-festpaket"
                className="text-purple-300 underline decoration-purple-500/50 underline-offset-2 hover:text-purple-200"
              >
                kompletta festpaket
              </Link>
              .
            </p>
          </div>
        ) : null}

        <div className="relative z-10">
          <div className="bg-slate-900/35 backdrop-blur-lg p-8 md:p-12 border border-slate-800 relative">
            {listRefreshing && (
              <div
                className="absolute inset-0 z-20 bg-slate-950/50 flex items-center justify-center"
                aria-hidden
              >
                <div className="h-10 w-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <ProductGrid
              products={products}
              selectedCategory={selectedCategory}
              searchQuery={searchInput}
              onSearchChange={setSearchInput}
              sortBy={sortBy}
              onSortChange={setSortBy}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
              totalCount={totalCount}
            />
            {error && products.length > 0 && (
              <p className="mt-4 text-center text-sm text-amber-400">{error}</p>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          33% {
            transform: translateY(-10px) rotate(120deg);
          }
          66% {
            transform: translateY(10px) rotate(240deg);
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default ProductsPage;
